import React, { useMemo, useEffect, useContext } from 'react'
import { View, StyleSheet, TouchableOpacity } from 'react-native'
import { Text, Input, Icon, Divider, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { getProductPrice, setProductPrice, applyPriceCalculations } from '../utils/priceTypeHelpers'
import { useAtomValue } from 'jotai'
import { storesDictAtom, priceTypesAtom, userRoleAtom } from '../global/AtomsProvider'
import { useNavigation } from '@react-navigation/native'
import { UserContext } from '../global/UserProvider'

/**
 * Component for editing multiple price types for a product
 * Gets stores and price types from atoms internally
 * @param {Object} product - The product being edited
 * @param {Function} onPriceChange - Callback when price changes
 */
export const PriceTypeEditor = ({ product, onPriceChange }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigation = useNavigation()
  const [context] = useContext(UserContext)
  const userRole = useAtomValue(userRoleAtom)
  
  // Get data from atoms
  const storesDict = useAtomValue(storesDictAtom)
  const priceTypes = useAtomValue(priceTypesAtom)

  const storesList = useMemo(() => Object.values(storesDict), [storesDict])
  
  // Auto-calculate additional prices when default price (salePrices) changes
  useEffect(() => {
    if (!product?.salePrices) return
    
    // Check if any price types have calculation rules
    const hasCalculations = priceTypes.some(pt => pt.calculationRules?.enabled)
    if (!hasCalculations) return
    
    // Apply calculations for each store
    let updatedProduct = { ...product }
    Object.keys(storesDict).forEach(storeId => {
      updatedProduct = applyPriceCalculations(updatedProduct, priceTypes, storeId)
    })
    
    if (JSON.stringify(updatedProduct.priceTypes) !== JSON.stringify(product.priceTypes)) {
      onPriceChange(updatedProduct)
    }
  }, [product?.salePrices?.[storesList[0]?.id]])

  const handlePriceChange = (priceTypeId, storeId, value, scope) => {
    const updatedProduct = setProductPrice(product, storeId, priceTypeId, value, scope)
    onPriceChange(updatedProduct)
  }

  const handleCopyPrices = (fromStoreId, toStoreId, priceTypeId) => {
    const price = getProductPrice(product, fromStoreId, priceTypeId)
    const priceType = priceTypes.find(pt => pt.id === priceTypeId)
    handlePriceChange(priceTypeId, toStoreId, price, priceType?.scope || 'store')
  }

  const getScopeForPriceType = (priceTypeId) => {
    const priceType = priceTypes.find(pt => pt.id === priceTypeId)
    return priceType?.scope || 'store'
  }

  const getPriceTypeIcon = (priceTypeId) => {
    const priceType = priceTypes.find(pt => pt.id === priceTypeId)
    return priceType?.icon || 'pricetags-outline'
  }

  if (!priceTypes || priceTypes.length === 0) {
    return null
  }

  return (
    <View style={styles.container}>
      <View style={styles.sectionTitleContainer}>
        <Text category='label' style={styles.sectionTitle}>
          {t('productsAndIngredients.additionalPrices') || 'Additional Prices'}
        </Text>
        {(userRole?.permissions?.['editSettings'] || context.user.role === 'admin') && (
          <TouchableOpacity 
            onPress={() => navigation.navigate('PriceTypes')} 
            style={styles.settingsIcon}
          >
            <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
          </TouchableOpacity>
        )}
      </View>
      <Text appearance='hint' category='c2' style={styles.sectionHint}>
        {t('productsAndIngredients.additionalPricesHint') || 'Optional additional pricing (wholesale, BNPL, etc.). Default price is set above in Sale Price field.'}
      </Text>

      {priceTypes.map((priceType) => {
        const scope = getScopeForPriceType(priceType.id)

        return (
          <View key={priceType.id} style={[styles.priceTypeCard, { backgroundColor: theme['background-basic-color-1'] }]}>
            <View style={styles.priceTypeHeaderLeft}>
              <Icon
                name={getPriceTypeIcon(priceType.id)}
                style={styles.priceTypeIcon}
              />
                <View style={styles.priceTypeHeaderContent}>
                <View style={styles.priceTypeHeaderRow}>
                  <Text category='s1' style={styles.priceTypeName}>{priceType.name}</Text>
                  {priceType.calculationRules?.enabled && (
                    <View style={styles.calculatedBadge}>
                      <Icon name='flash-outline' style={styles.calculatedIcon} fill='#FFAA00' />
                      <Text category='c2' appearance='hint'>{t('productsAndIngredients.autoCalculated') || 'Auto'}</Text>
                    </View>
                  )}
                </View>
                <Text appearance='hint' category='c1'>
                  {scope === 'organization' 
                    ? (t('productsAndIngredients.organizationWide') || 'Organization-wide')
                    : (t('productsAndIngredients.perStore') || 'Per Store')}
                </Text>
              </View>
            </View>

            <View style={styles.priceTypeContent}>
              <Divider style={styles.divider} />
              
              {scope === 'organization' ? (
                // Organization-wide price
                <View style={styles.priceInputContainer}>
                  <Text category='label' style={styles.priceLabel}>
                    {t('productsAndIngredients.price') || 'Price'}
                  </Text>
                  { priceType.calculationRules?.enabled ? (
                    <Text style={styles.priceInput}>{getProductPrice(product, storesList[0].id, priceType.id, 'organization', priceTypes).toString()}</Text>
                      ) : (
                    <Input
                      value={getProductPrice(product, storesList[0].id, priceType.id, 'organization', priceTypes).toString()}
                      onChangeText={(value) => handlePriceChange(priceType.id, 'organization', value, 'organization')}
                      keyboardType='numeric'
                      placeholder='0'
                      style={styles.priceInput}
                    />
                  )}
                </View>
              ) : (
                // Store-specific prices
                <>
                  {storesList.map((store, storeIndex) => (
                    <View key={store.id} style={styles.storePriceContainer}>
                      <View style={styles.storePriceHeader}>
                        <Text category='label' style={styles.priceLabel}>
                          {store.name}
                        </Text>
                        {storesList.length > 1 && storeIndex > 0 && !priceType.calculationRules?.enabled && (
                          <TouchableOpacity
                            onPress={() => handleCopyPrices(storesList[0].id, store.id, priceType.id)}
                          >
                            <Text appearance='hint' category='c2' style={[styles.copyLink, { color: theme['color-primary-500'] }]}>
                              {t('productsAndIngredients.copyFromFirst') || 'Copy from first'}
                            </Text>
                          </TouchableOpacity>
                        )}
                      </View>
                      { priceType.calculationRules?.enabled ? (
                        <Text style={styles.priceInput}>{getProductPrice(product, storesList[0].id, priceType.id, 'store', priceTypes).toString()}</Text>
                      ) : (
                        <Input
                          value={getProductPrice(product, storesList[0].id, priceType.id, 'store', priceTypes).toString()}
                          onChangeText={(value) => handlePriceChange(priceType.id, store.id, value, 'store')}
                          keyboardType='numeric'
                          placeholder='0'
                          style={styles.priceInput}
                        />
                      )}
                    </View>
                  ))}
                </>
              )}
            </View>
          </View>
        )
      })}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: 15
  },
  sectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  sectionTitle: {
    marginBottom: 0,
  },
  settingsIcon: {
    marginLeft: 5,
    paddingHorizontal: 3,
  },
  sectionHint: {
    marginBottom: 5,
  },
  priceTypeCard: {
    alignSelf: 'stretch',
    marginVertical: 6,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 6,
    marginBottom: 5,
  },
  priceTypeHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1
  },
  priceTypeIcon: {
    width: 24,
    height: 24,
    marginRight: 12
  },
  priceTypeContent: {
    marginTop: 8
  },
  divider: {
    marginVertical: 12
  },
  priceInputContainer: {
    marginBottom: 12
  },
  storePriceContainer: {
    marginBottom: 12
  },
  storePriceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4
  },
  priceLabel: {
    marginBottom: 4
  },
  priceInput: {
    flex: 1
  },
  copyLink: {
    textDecorationLine: 'underline'
  },
  calculatedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#FFF5E6',
    borderRadius: 4,
    // Allow the badge to shrink/wrap on small screens
    flexShrink: 1,
    maxWidth: '100%',
    marginTop: 4
  },
  calculatedIcon: {
    width: 12,
    height: 12,
    marginRight: 4
  },
  priceTypeHeaderContent: {
    flex: 1
  },
  priceTypeHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    // Enable wrapping so long labels move to the next line instead of overflowing
    flexWrap: 'wrap'
  },
  priceTypeName: {
    marginRight: 8
  }
})

